import {
  CorporateFare as CorporateIcon,
  Factory as FactoryIcon,
  Assignment as ProjectIcon,
  Article as SourceIcon,
} from "@mui/icons-material";
import { Box, Chip, Tab, Tabs, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import React, { useMemo, useState } from "react";
import type { EntityTypeKey } from "../../../types/entityRegistry";
import { DetailedFacility } from "../../../types/facilityTypes";
import { projectStatusMapping } from "../../../utils/projectStatusMapping";
import { projectTypeMapping } from "../../../utils/projectTypeMapping";
import ProjectStatusChip from "../../common/ProjectStatusChip";
import ClickableEntityName from "./ClickableEntityName";
import EntityRelationTable from "./EntityRelationTable";

interface EntityRelationshipTabsProps {
  detailedEntity: DetailedFacility; // Will be generalized later
  onEntityClick?: (entityId: string) => void;
  context?: EntityTypeKey;
  mode?: "overlay" | "standalone";
}

interface EntityRelation {
  id: string;
  relationshipId: string;
  name: string;
  relationType: string;
  industry: string;
  region: string;
  relationStatus?: string | string[]; // Can be array for projects
  relationSummary?: string;
  investmentAmount?: string;
  investmentCurrency?: string;
  ownershipShare?: number;
  confidenceScore?: number;
  relationStart?: string;
  relationEnd?: string;
  count?: number;
  address?: string;
  projectSummary?: string;
  publicationDate?: string;
}

/**
 * Extract unique filter options from table data
 * Handles both single values and arrays (e.g., project_status: string[])
 */
const getFilterOptions = <T,>(data: T[], accessor: keyof T): string[] => {
  const values = new Set<string>();

  data.forEach((row) => {
    const value = row[accessor];
    if (value == null || value === "") return;

    // Handle arrays (e.g., project_status: string[])
    if (Array.isArray(value)) {
      value.forEach((v) => v && values.add(String(v)));
    } else {
      values.add(String(value));
    }
  });

  return Array.from(values).sort();
};

/**
 * Filter function for columns that may contain array values
 * Checks if filter value is included in array or matches string value
 */
const arrayIncludesFilter = (
  row: any,
  id: string,
  filterValue: string,
): boolean => {
  const cellValue = row.getValue(id);
  if (!filterValue) return true;

  const filterLower = filterValue.toLowerCase();

  // Handle arrays (e.g., ["in_progress", "delayed"])
  if (Array.isArray(cellValue)) {
    return cellValue.some((v) => String(v).toLowerCase().includes(filterLower));
  }

  // Handle strings
  return String(cellValue || "")
    .toLowerCase()
    .includes(filterLower);
};

const EntityRelationshipTabs: React.FC<EntityRelationshipTabsProps> = ({
  detailedEntity,
  onEntityClick,
  context,
  mode = "overlay",
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Transform data for Organizations tab
  const organizationsData = useMemo<EntityRelation[]>(
    () =>
      detailedEntity?.organizations
        ?.filter((item) => item.organization && item.organization.id)
        .map((item) => ({
          id: item.organization.id,
          relationshipId: item.relationship?.id || "",
          name: item.organization.name || "Unknown Organization",
          relationType: item.relationship?.semantic_role || "Unknown",
          industry: item.organization.industry || "N/A",
          region: item.organization.home_country || "N/A",
          address: item.organization.address,
          relationStatus: item.relationship?.relation_status || "active",
          relationSummary: item.relationship?.relation_summary,
          investmentAmount: item.relationship?.investment_amount,
          investmentCurrency: item.relationship?.investment_currency,
          ownershipShare: item.relationship?.ownership_share,
          confidenceScore: item.relationship?.confidence_score,
          relationStart: item.relationship?.relation_start,
          relationEnd: item.relationship?.relation_end,
          count: item.relationship?.count,
        })) || [],
    [detailedEntity?.organizations],
  );

  // Transform data for Facilities tab
  const facilitiesData = useMemo<EntityRelation[]>(
    () =>
      detailedEntity?.facilities
        ?.filter((item) => item.facility && item.facility.id)
        .map((item) => ({
          id: item.facility.id,
          relationshipId: item.relationship?.id || "",
          name: item.facility.name || "Unknown Facility",
          relationType: item.relationship?.semantic_role || "supply_chain",
          industry: item.facility.industry || "N/A",
          region: item.facility.region || item.facility.country_iso2 || "N/A",
          address: item.facility.address,
          relationStatus: item.relationship?.relation_status || "active",
          relationSummary: item.relationship?.relation_summary,
          investmentAmount: item.relationship?.investment_amount,
          investmentCurrency: item.relationship?.investment_currency,
          ownershipShare: item.relationship?.ownership_share,
          confidenceScore: item.relationship?.confidence_score,
          relationStart: item.relationship?.relation_start,
          relationEnd: item.relationship?.relation_end,
          count: item.relationship?.count,
        })) || [],
    [detailedEntity?.facilities],
  );

  // Transform data for Projects tab
  const projectsData = useMemo<EntityRelation[]>(
    () =>
      detailedEntity?.projects
        ?.filter((item) => item.project && item.project.id)
        .map((item) => ({
          id: item.project.id,
          relationshipId: item.relationship?.id || "",
          name: item.project.name || "Unknown Project",
          relationType: item.relationship?.project_role || "participant",
          industry: item.project.project_type?.[0] || "Unknown",
          region: item.project.region || item.project.country || "N/A",
          address: item.project.address,
          projectSummary: item.project.description || item.project.summary,
          // Keep project_status as array for proper filtering
          relationStatus: item.project.project_status || [],
          relationSummary: item.relationship?.relation_summary,
          investmentAmount: item.relationship?.investment_amount,
          investmentCurrency: item.relationship?.investment_currency,
          ownershipShare: item.relationship?.ownership_share,
          confidenceScore: item.relationship?.confidence_score,
          relationStart:
            item.project.project_start ||
            item.project.start_date ||
            item.project.startDate,
          relationEnd:
            item.project.project_end ||
            item.project.end_date ||
            item.project.endDate,
          count: item.relationship?.count,
          publicationDate: item.project.publication_date,
        })) || [],
    [detailedEntity?.projects],
  );

  // Transform data for Sources tab
  const sourcesData = useMemo<EntityRelation[]>(
    () =>
      detailedEntity?.sources
        ?.filter((item) => item.source && item.source.id)
        .map((item) => ({
          id: item.source.id,
          relationshipId: item.relationship?.id || "",
          name: item.source.title || item.source.name || "Unknown Source",
          relationType: item.source.type || "document",
          industry: "N/A",
          region: "N/A",
          address: undefined,
          relationStatus: item.source.source || item.source.domain || "N/A",
          relationStart:
            item.source.publication_date || item.source.date || "N/A",
          relationSummary:
            item.relationship?.relation_summary ||
            item.source.summary ||
            item.source.description,
        })) || [],
    [detailedEntity?.sources],
  );

  // Column definitions for Material React Table
  const organizationsColumns = useMemo<MRT_ColumnDef<EntityRelation>[]>(() => {
    const tableData = organizationsData;

    return [
      {
        accessorKey: "name",
        header: "Organization Name",
        size: 250,
        Cell: ({ row }) => (
          <ClickableEntityName
            name={row.original.name}
            entityId={row.original.id}
            onEntityClick={onEntityClick}
            mode={mode}
          />
        ),
        filterFn: "fuzzy",
      },
      {
        accessorKey: "relationType",
        header: "Relation Type",
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationType").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "relationStatus",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          if (!status)
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "active":
                return "success";
              case "inactive":
                return "error";
              case "pending":
                return "warning";
              case "terminated":
                return "error";
              default:
                return "default";
            }
          };

          return (
            <Chip
              label={status}
              size="small"
              color={getStatusColor(status) as any}
              variant="filled"
              sx={{ textTransform: "capitalize" }}
            />
          );
        },
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationStatus").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "count",
        header: "Sources",
        size: 90,
        Cell: ({ cell }) => {
          const count = cell.getValue<number>();
          if (!count)
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          return (
            <Chip
              label={count}
              size="small"
              variant="outlined"
              color="default"
            />
          );
        },
        enableColumnFilter: false,
      },
    ];
  }, [organizationsData, onEntityClick]);

  const facilitiesColumns = useMemo<MRT_ColumnDef<EntityRelation>[]>(() => {
    const tableData = facilitiesData;

    return [
      {
        accessorKey: "name",
        header: "Facility Name",
        size: 250,
        Cell: ({ row }) => (
          <ClickableEntityName
            name={row.original.name}
            entityId={row.original.id}
            onEntityClick={onEntityClick}
            mode={mode}
          />
        ),
        filterFn: "fuzzy",
      },
      {
        accessorKey: "relationType",
        header: "Relation Type",
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationType").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "relationStatus",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          if (!status)
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "active":
                return "success";
              case "inactive":
              case "terminated":
                return "error";
              case "pending":
                return "warning";
              default:
                return "default";
            }
          };

          return (
            <Chip
              label={status}
              size="small"
              color={getStatusColor(status)}
              sx={{ textTransform: "capitalize" }}
            />
          );
        },
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationStatus").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "count",
        header: "Sources",
        size: 90,
        Cell: ({ cell }) => {
          const count = cell.getValue<number>();
          if (!count)
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          return (
            <Chip
              label={count}
              size="small"
              variant="outlined"
              color="default"
            />
          );
        },
        enableColumnFilter: false,
      },
    ];
  }, [facilitiesData, onEntityClick]);

  const projectsColumns = useMemo<MRT_ColumnDef<EntityRelation>[]>(() => {
    const tableData = projectsData;

    return [
      {
        accessorKey: "name",
        header: "Project Name",
        size: 200,
        Cell: ({ row }) => (
          <ClickableEntityName
            name={row.original.name}
            entityId={row.original.id}
            onEntityClick={onEntityClick}
            variant="body2"
            fontWeight={500}
          />
        ),
        filterFn: "fuzzy",
      },
      {
        accessorKey: "industry",
        header: "Project Type",
        size: 120,
        Cell: ({ cell }) => {
          const projectType = cell.getValue<string>();
          const displayName = projectTypeMapping.getDisplayName(projectType);
          return (
            <Chip
              label={displayName}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ textTransform: "capitalize" }}
            />
          );
        },
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "industry").map(
          (value) => ({
            text: projectTypeMapping.getDisplayName(value),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "relationType",
        header: "Relation Type",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationType").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "relationStatus",
        header: "Status",
        size: 100,
        Cell: ({ cell, row }) => {
          const status = cell.getValue();
          if (!status)
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );

          const getProjectStatusColor = (
            status: string,
          ):
            | "success"
            | "error"
            | "warning"
            | "info"
            | "primary"
            | "secondary"
            | "default" => {
            const normalizedStatus = status.toLowerCase();
            switch (normalizedStatus) {
              case "in_progress":
              case "ongoing":
                return "success";
              case "completed":
                return "primary";
              case "cancelled":
              case "failed":
                return "error";
              case "delayed":
              case "paused":
              case "suspended":
              case "stalled":
              case "at_risk":
                return "warning";
              case "planned":
              case "proposed":
                return "default";
              case "pending":
                return "info";
              case "alleged":
                return "secondary";
              default:
                return "default";
            }
          };

          // Handle both single status and array of statuses
          if (Array.isArray(status)) {
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {status.map((singleStatus, index) => (
                  <ProjectStatusChip
                    key={index}
                    statusId={singleStatus}
                    lastUpdatedDate={row.original.publicationDate}
                    size="small"
                  />
                ))}
              </Box>
            );
          } else if (typeof status === "string") {
            return (
              <ProjectStatusChip
                statusId={status}
                lastUpdatedDate={row.original.publicationDate}
                size="small"
              />
            );
          } else {
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );
          }
        },
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationStatus").map(
          (value) => ({
            text: projectStatusMapping.getDisplayName(value),
            value: value,
          }),
        ),
        filterFn: arrayIncludesFilter, // Use array-aware filter for status arrays
      },
      {
        accessorKey: "count",
        header: "Sources",
        size: 90,
        Cell: ({ cell }) => {
          const count = cell.getValue<number>();
          if (!count)
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          return (
            <Chip
              label={count}
              size="small"
              variant="outlined"
              color="default"
            />
          );
        },
        enableColumnFilter: false,
      },
    ];
  }, [projectsData, onEntityClick]);

  const sourcesColumns = useMemo<MRT_ColumnDef<EntityRelation>[]>(() => {
    const tableData = sourcesData;

    return [
      {
        accessorKey: "name",
        header: "Title",
        size: 250,
        Cell: ({ row }) => (
          <ClickableEntityName
            name={row.original.name}
            entityId={row.original.id}
            onEntityClick={onEntityClick}
            variant="body2"
            fontWeight={500}
          />
        ),
        filterFn: "fuzzy",
      },
      {
        accessorKey: "relationType",
        header: "Type",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>().replace("-", " ")}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
        filterVariant: "select",
        filterSelectOptions: getFilterOptions(tableData, "relationType").map(
          (value) => ({
            text:
              value.charAt(0).toUpperCase() +
              value.slice(1).replace(/-/g, " ").replace(/_/g, " "),
            value: value,
          }),
        ),
      },
      {
        accessorKey: "relationStatus", // This is a hack, should create a proper interface later
        header: "Source",
        size: 150,
        filterFn: "fuzzy",
        Cell: ({ cell }) => cell.getValue<string>(),
      },
      {
        accessorKey: "relationStart",
        header: "Date",
        size: 120,
      },
    ];
  }, [sourcesData, onEntityClick]);

  return (
    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <Box sx={{ paddingLeft: 3, paddingTop: 2 }}>
        <Typography
          variant="h6"
          sx={{ color: "text.primary", fontWeight: 600 }}
        >
          Relations
        </Typography>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            icon={<CorporateIcon />}
            label={`Organizations (${detailedEntity?.organizations?.length || 0})`}
            iconPosition="start"
          />
          <Tab
            icon={<FactoryIcon />}
            label={`Facilities (${detailedEntity?.facilities?.length || 0})`}
            iconPosition="start"
          />
          <Tab
            icon={<ProjectIcon />}
            label={`Projects (${detailedEntity?.projects?.length || 0})`}
            iconPosition="start"
          />
          <Tab
            icon={<SourceIcon />}
            label={`Sources (${detailedEntity?.sources?.length || 0})`}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Box sx={{ px: 2, pt: 1, flexGrow: 1, overflow: "auto" }}>
        {activeTab === 0 && (
          <Box>
            {organizationsData.length > 0 ? (
              <EntityRelationTable
                columns={organizationsColumns}
                data={organizationsData}
                enableExpanding={true}
                context={context}
                mode={mode}
              />
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  🏢 No Organization Relationships
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No organization relationships found for this facility.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {activeTab === 1 && (
          <Box>
            {facilitiesData.length > 0 ? (
              <EntityRelationTable
                columns={facilitiesColumns}
                data={facilitiesData}
                enableExpanding={true}
                context={context}
                mode={mode}
              />
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Relations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No relations found for this entity type.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {activeTab === 2 && (
          <Box>
            {projectsData.length > 0 ? (
              <EntityRelationTable
                columns={projectsColumns}
                data={projectsData}
                enableExpanding
                context={context}
                mode={mode}
              />
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  📋 No Project Relationships
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No project relationships found for this facility.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {activeTab === 3 && (
          <Box>
            {sourcesData.length > 0 ? (
              <EntityRelationTable
                columns={sourcesColumns}
                data={sourcesData}
                enableExpanding
                context="source"
                mode={mode}
              />
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  📰 No Source Relationships
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No source relationships found for this facility.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EntityRelationshipTabs;
